import { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom-v5-compat";

import { BTText } from "@btnet/core-components";

import { BdsIconFilePageLayout } from "@buildertrend/components";

import { SpaInfoContext } from "helpers/globalContext/SPAInfoContext";
import { queryClient } from "helpers/queryClient";

import { ClientObservableBackgroundJobStatus } from "types/enum";

import { getJobPickerState, setJobPickerState } from "utilities/jobPicker/jobPicker";
import { routes } from "utilities/routes";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { type ClientObservableBackgroundJobDetails } from "commonComponents/utilities/ClientObservableBackgroundJobStatusPopup/ClientObservableBackgroundJobStatusPopup.types";

import { getPlansRevisionsActiveGetV2QueryKey } from "handlers/plans/plans";

import { getPlansListQueryKey } from "entity/Plans/PlansList/PlansList";

import "./PlanUploadItem.less";

export function PlanUploadItem({
    process,
    jobId,
}: Readonly<{ process: ClientObservableBackgroundJobDetails; jobId: number }>) {
    const navigate = useNavigate();

    const { isSpa } = useContext(SpaInfoContext);

    const isComplete = process.status === ClientObservableBackgroundJobStatus.Success;

    const isAddFile = process.additionalData?.isAddFile ?? false;

    // Mass-revisions jobs carry a draftId — on completion they go to the full-page review/confirm flow
    // rather than straight to the plan set.
    const revisionDraftId = process.additionalData?.draftId;

    const handleReviewRevisions = () => {
        if (revisionDraftId) {
            navigate(
                routes.plans.getRevisionsReviewLink(revisionDraftId, process.entityId ?? undefined),
            );
        }
    };

    useEffect(() => {
        if (isComplete) {
            void queryClient.invalidateQueries({
                queryKey: getPlansListQueryKey(process.entityId ?? undefined),
            });
            // A finished revisions import means a draft now exists — refresh the Plans set's
            // active-draft query so its review banner appears immediately, without a page refresh.
            if (revisionDraftId) {
                void queryClient.invalidateQueries({
                    queryKey: getPlansRevisionsActiveGetV2QueryKey(),
                });
            }
        }
    }, [isComplete, process.entityId, revisionDraftId]);

    const handleViewPlan = () => {
        const setId = process.additionalData?.setId;
        const targetJobId =
            process.entityId === jobId ? undefined : (process.entityId ?? undefined);

        const targetIsTemplate = process.additionalData?.isTemplate ?? false;
        const jobPickerState = getJobPickerState();
        const needsModeSwitch = jobPickerState.isTemplateMode !== targetIsTemplate;

        if (needsModeSwitch) {
            setJobPickerState({ ...jobPickerState, isTemplateMode: targetIsTemplate });
        }

        const targetUrl = routes.plans.getPlansListLink(setId, targetJobId);
        const finalUrl = isAddFile ? `${targetUrl}?organize=true` : targetUrl;

        if (needsModeSwitch || !isSpa) {
            // Full page reload so the job picker re-initializes with the correct mode
            window.location.assign(routes.appBase + finalUrl);
        } else {
            navigate(finalUrl);
        }
    };

    return (
        <div className="flex flex-row PlanUploadItem justify-between items-center gap-1">
            <div className="flex flex-row PlanUploadItem-content items-center gap-1">
                <BdsIconFilePageLayout />
                <BTText className="PlanUploadItemText" size="normal-lg" text={process.details} />
            </div>
            {isComplete &&
                (revisionDraftId ? (
                    <BTButton
                        className="PlanUploadItem-button"
                        data-testid={`review-revisions-button-${process.processID}`}
                        onClick={handleReviewRevisions}
                        type="tertiary"
                    >
                        Review revisions
                    </BTButton>
                ) : (
                    <BTButton
                        className="PlanUploadItem-button"
                        data-testid={`view-plan-button-${process.processID}`}
                        onClick={handleViewPlan}
                        type="tertiary"
                    >
                        {isAddFile ? "Organize pages" : "View plan"}
                    </BTButton>
                ))}
        </div>
    );
}
